import React, { useContext, useEffect } from "react";
import {
    ButtonProps,
    ButtonRenderProps,
    ClearFiltersButton,
    ContentSection,
    createListTableColumns,
    Grid,
    GridColumn,
    GridRow,
    ListTable,
    renderDateTime,
    SearchBox,
    Stack,
    Text
} from "sprint-common-web";

import { FramerateProfileListingContext } from "../context/FramerateProfilesListingContext";
import { FramerateProfileListingItem } from "../context/FramerateProfilesListingItem";
import { AddFramerateProfileButton } from "./buttons/AddFramerateProfileButton";
import { EditFramerateProfileButton } from "./buttons/EditFramerateProfileButton";
import { useFramerateProfilesListingFilters } from "./useFramerateProfilesListingFilters";

export const FramerateProfilesListing: React.FC = () => {
    const { filters, setName, clearFilters, setSort, setPage, setPageSize } = useFramerateProfilesListingFilters();
    const { items, allItems, totalItems, isLoading, load } = useContext(FramerateProfileListingContext);

    useEffect(() => {
        load(filters);
    }, [filters.name, filters.sortBy, filters.sortOrder, allItems, allItems.length]);

    const columns = createListTableColumns<FramerateProfileListingItem>([
        {
            type: "basic",
            key: "name",
            name: "Name",
            minWidth: 200,
            maxWidth: 250,
            isSortable: true
        },
        {
            type: "basic",
            key: "mappings",
            name: "Mappings",
            minWidth: 200,
            maxWidth: 450,
            onRender: renderFramerateProfileMappings
        },
        {
            type: "basic",
            key: "updatedDate",
            name: "Updated",
            minWidth: 200,
            maxWidth: 250,
            isSortable: true,
            onRender: renderDateTime
        },
        {
            type: "buttons",
            key: "actions",
            name: "Actions",
            minWidth: 80,
            maxWidth: 90,
            buttons: [
                {
                    id: "EditFramerateProfile",
                    render: EditFramerateProfileButton
                }
            ]
        }
    ]);

    const addFramerateProfileButtonProps: ButtonRenderProps<ButtonProps> = {
        render: AddFramerateProfileButton
    };

    return (
        <ContentSection header={"Framerate Profiles"} buttons={[addFramerateProfileButtonProps]}>
            <Grid>
                <GridRow>
                    <GridColumn sm={4}>
                        <SearchBox placeholder="Name" onChange={setName} value={filters.name} />
                    </GridColumn>

                    <GridColumn sm={8}>
                        <Stack horizontalAlign="end">
                            <ClearFiltersButton onClear={clearFilters} isLabeled={true} />
                        </Stack>
                    </GridColumn>
                </GridRow>
                <GridRow>
                    <GridColumn sm={12}>
                        <ListTable
                            columns={columns}
                            items={items}
                            isLoading={isLoading}
                            pagination={{
                                page: filters.page,
                                pageSize: filters.pageSize,
                                totalItems,
                                onPageChange: setPage,
                                onPageSizeChange: setPageSize
                            }}
                            onSortChange={setSort}
                            sort={{ by: filters.sortBy, order: filters.sortOrder }}
                        />
                    </GridColumn>
                </GridRow>
            </Grid>
        </ContentSection>
    );
};

const renderFramerateProfileMappings = (item: FramerateProfileListingItem) => {
    const mappingsStrings = [
        `23.976: ${item.mappings[23.98]}`,
        `24: ${item.mappings[24]}`,
        `25: ${item.mappings[25]}`,
        `29.97: ${item.mappings[29.97]}`,
        `30: ${item.mappings[30]}`,
        `50: ${item.mappings[50]}`,
        `59.94: ${item.mappings[59.94]}`,
        `60: ${item.mappings[60]}`,
        `OTHER: ${item.mappings.OTHER}`
    ];

    const items = mappingsStrings.map((mappingString) => {
        return (
            <Stack key={mappingString}>
                <Text variant={"smallPlus"} value={mappingString} styles={{ root: { padding: 2 } }} />
            </Stack>
        );
    });
    return <Stack>{items}</Stack>;
};
